import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  BellRing, 
  Check, 
  CheckCheck, 
  X, 
  Calendar,
  FileText,
  Camera,
  MessageCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Settings,
  Monitor,
  Headphones,
  DollarSign,
  Trash2,
  Wrench
} from 'lucide-react';
import { Notification, NotificationType } from '@/lib/types/notifications';
import { notificationService } from '@/services/notificationService';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import DesktopNotificationSettings from './DesktopNotificationSettings';
import SoundNotificationSettings from './SoundNotificationSettings';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to safely convert timestamps to Date objects
const safeTimestampToDate = (timestamp: any): Date => {
  if (!timestamp) return new Date();
  
  // If it's already a Date object
  if (timestamp instanceof Date) return timestamp;
  
  // If it's a Firestore Timestamp
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // If it's an ISO string
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  // If it's a timestamp object with seconds
  if (timestamp && timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  
  // Fallback to current date
  console.warn('Unknown timestamp format:', timestamp);
  return new Date();
};

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDesktopSettings, setShowDesktopSettings] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();


  // Expose fetch function globally for debugging
  React.useEffect(() => {
    (window as any).fetchNotifications = fetchNotifications;
    return () => {
      delete (window as any).fetchNotifications;
    };
  }, [currentUser?.email]);

  useEffect(() => {
    if (!currentUser?.email) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Set up a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      setNotifications([]);
    }, 10000);
    
    // First, try to fetch notifications immediately
    const initialFetch = async () => {
      try {
        const userNotifications = await notificationService.getUserNotifications(currentUser.email);
        setNotifications(userNotifications);
        setLoading(false);
        clearTimeout(loadingTimeout);
      } catch (error) {
        console.warn('NotificationsPanel: Initial fetch failed, falling back to real-time listener:', error);
      }
    };
    
    initialFetch();
    
    // Set up real-time listeners for notifications
    const unsubscribeNotifications = notificationService.listenToUserNotifications(
      currentUser.email,
      (userNotifications: Notification[]) => {
        setNotifications(userNotifications);
        setLoading(false);
        clearTimeout(loadingTimeout);
      }
    );

    const unsubscribeUnreadCount = notificationService.listenToUnreadCount(
      currentUser.email,
      (count: number) => {
        setUnreadCount(count);
      }
    );

    return () => {
      clearTimeout(loadingTimeout);
      unsubscribeNotifications();
      unsubscribeUnreadCount();
    };
  }, [currentUser?.email]);

  const fetchNotifications = async () => {
    if (!currentUser?.email) return;
    
    try {
      setLoading(true);
      const userNotifications = await notificationService.getUserNotifications(currentUser.email);
      setNotifications(userNotifications);
    } catch (error) {
      console.error('NotificationsPanel: Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!currentUser?.email) return;
    
    try {
      await notificationService.markAllAsRead(currentUser.email);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleClearAll = async () => {
    if (!currentUser?.email) return;
    
    if (!confirm('Are you sure you want to clear all notifications? This cannot be undone.')) {
      return;
    }
    
    try {
      await notificationService.clearAllNotifications(currentUser.email);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await handleMarkAsRead(notification.id);
    }

    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update unread count if this was an unread notification
      const deletedNotification = notifications.find(n => n.id === notificationId);
      if (deletedNotification && !deletedNotification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.TaskAssigned:
      case NotificationType.TaskUpdated:
      case NotificationType.TaskCompleted:
      case NotificationType.TaskDue:
        return <CheckCheck className="h-4 w-4" />;
      case NotificationType.EventInvite:
      case NotificationType.EventUpdated:
      case NotificationType.GroupCalendarEvent:
        return <Calendar className="h-4 w-4" />;
      case NotificationType.TemplateReview:
      case NotificationType.TemplateCorrections:
      case NotificationType.TemplateApproved:
      case NotificationType.TemplateRejected:
        return <FileText className="h-4 w-4" />;
      case NotificationType.PhotosUploaded:
      case NotificationType.PhotosProcessed:
        return <Camera className="h-4 w-4" />;
      case NotificationType.FinChatMessage:
      case NotificationType.FinChatMention:
        return <MessageCircle className="h-4 w-4" />;
      case NotificationType.JewelryRequestSubmitted:
        return <Bell className="h-4 w-4" />;
      case NotificationType.CCOSaleAdded:
        return <DollarSign className="h-4 w-4" />;
      case NotificationType.RepairQuoteReady:
        return <Wrench className="h-4 w-4" />;
      case NotificationType.SystemUpdate:
      case NotificationType.SystemMaintenance:
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.TaskAssigned:
      case NotificationType.TaskUpdated:
        return 'text-blue-600 bg-blue-50';
      case NotificationType.TaskCompleted:
        return 'text-emerald-600 bg-emerald-50';
      case NotificationType.TaskDue:
        return 'text-orange-600 bg-orange-50';
      case NotificationType.EventInvite:
      case NotificationType.EventUpdated:
      case NotificationType.GroupCalendarEvent:
        return 'text-purple-600 bg-purple-50';
      case NotificationType.TemplateReview:
      case NotificationType.TemplateCorrections:
        return 'text-amber-600 bg-amber-50';
      case NotificationType.TemplateApproved:
        return 'text-emerald-600 bg-emerald-50';
      case NotificationType.TemplateRejected:
        return 'text-red-600 bg-red-50';
      case NotificationType.PhotosUploaded:
      case NotificationType.PhotosProcessed:
        return 'text-indigo-600 bg-indigo-50';
      case NotificationType.FinChatMessage:
      case NotificationType.FinChatMention:
        return 'text-teal-600 bg-teal-50';
      case NotificationType.JewelryRequestSubmitted:
        return 'text-amber-600 bg-amber-50';
      case NotificationType.CCOSaleAdded:
        return 'text-emerald-600 bg-emerald-50';
      case NotificationType.RepairQuoteReady:
        return 'text-cyan-600 bg-cyan-50';
      case NotificationType.SystemUpdate:
      case NotificationType.SystemMaintenance:
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="notifications-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            key="notifications-panel"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-[420px] bg-white shadow-2xl z-[60] flex flex-col border-l border-gray-200"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-white to-gray-50/80">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-blue-50 border border-blue-100">
                    <BellRing className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
                    {unreadCount > 0 && (
                      <p className="text-xs text-blue-600 font-medium">{unreadCount} unread</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setShowSoundSettings(true)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Sound Settings"
                  >
                    <Headphones className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setShowDesktopSettings(true)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    title="Desktop Notifications"
                  >
                    <Monitor className="h-4 w-4" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Action Buttons */}
              {notifications.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors border border-blue-100"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      Mark all read
                    </button>
                  )}
                  <button
                    onClick={handleClearAll}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mb-4"></div>
                  <p className="text-gray-500 text-sm">Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6">
                  <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 mb-4">
                    <Bell className="h-10 w-10 text-gray-300" />
                  </div>
                  <p className="text-gray-700 text-base font-medium mb-1">No notifications yet</p>
                  <p className="text-gray-400 text-sm mb-5">
                    You'll see important updates and messages here
                  </p>
                  <button
                    onClick={fetchNotifications}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                  >
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="p-3 space-y-1">
                  {notifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 50 }}
                      layout
                      className={`group relative p-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                        notification.read 
                          ? 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200' 
                          : 'bg-blue-50/50 border-blue-100 hover:bg-blue-50 hover:border-blue-200'
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon */}
                        <div className={`p-2 rounded-lg flex-shrink-0 ${getNotificationColor(notification.type)}`}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className={`text-sm font-medium leading-tight ${
                              notification.read ? 'text-gray-600' : 'text-gray-900'
                            }`}>
                              {notification.title}
                            </h3>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {!notification.read && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full" />
                              )}
                              {/* Delete button */}
                              <button
                                onClick={(e) => handleDeleteNotification(notification.id, e)}
                                className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all text-gray-300 hover:text-red-500"
                                title="Delete notification"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          
                          <p className={`text-xs mt-0.5 leading-relaxed ${
                            notification.read ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-[11px] text-gray-400">
                              {formatDistanceToNow(safeTimestampToDate(notification.createdAt), { addSuffix: true })}
                            </span>
                            
                            {notification.actionUrl && (
                              <span className="flex items-center gap-1 text-[11px] text-blue-500 font-medium">
                                <ExternalLink className="h-3 w-3" />
                                {notification.actionText || 'View'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
      
      {/* Sound Notification Settings Modal */}
      {showSoundSettings && (
        <>
          <motion.div
            key="sound-settings-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70]"
            onClick={() => setShowSoundSettings(false)}
          />
          <motion.div
            key="sound-settings-panel"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-[70] p-4"
          >
            <SoundNotificationSettings 
              onClose={() => setShowSoundSettings(false)}
            />
          </motion.div>
        </>
      )}

      {/* Desktop Notification Settings Modal */}
      {showDesktopSettings && (
        <>
          <motion.div
            key="desktop-settings-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[70]"
            onClick={() => setShowDesktopSettings(false)}
          />
          <motion.div
            key="desktop-settings-panel"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-[70] p-4"
          >
            <DesktopNotificationSettings 
              onClose={() => setShowDesktopSettings(false)}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default NotificationsPanel;
